import type React from "react";

import { renderToString } from "react-dom/server";

import { colorsPalettes } from "@similarweb/styles";
import { ChartTooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { abbrNumberFilter, minAbbrNumberFilter } from "app/filters/ngFilters";
import { getVolumeValue } from "app/pages/website-analysis/traffic-sources/search/common/utils";
import { translate } from "app/filters/i18nFilter";

import { KEYWORDS_GAP_VALUE_KEY, MetricTypes, VOLUME_GAP_VALUE_KEY } from "../const";
import type { IVennDiagramTransformedItem } from "../Types";

import { ChartTooltipWrapper, TooltipTitleContainer, TooltipTitleText } from "./StyledComponents";
import type { Point } from "highcharts";

const DEFAULT_COLOR = colorsPalettes.carbon[500];

interface ITooltipRowData {
    domain: {
        name: string;
        color: string;
    };
    value: number;
}

const CustomTitle: React.FC<{ title: string; value: string }> = ({ title, value }) => (
    <TooltipTitleContainer>
        <Flex
            justifyContent="space-between"
            alignItems="center"
        >
            <TooltipTitleText>{title}</TooltipTitleText>
            <TooltipTitleText>{value}</TooltipTitleText>
        </Flex>
    </TooltipTitleContainer>
);

const getFormattedValue = (value: number, valueKey: "volumeValue" | "keywordsValue"): string => {
    return (
        valueKey === VOLUME_GAP_VALUE_KEY ? minAbbrNumberFilter()(getVolumeValue(value), 50) : abbrNumberFilter()(value)
    ) as string;
};

export const createVennTooltipFormatter = (
    allData: IVennDiagramTransformedItem[],
    selectedMetricIndex: MetricTypes,
) => {
    return function (this: Point) {
        const point = this as Highcharts.Point & IVennDiagramTransformedItem;
        const isIntersection = point.sets && point.sets.length > 1;
        const valueKey = selectedMetricIndex === MetricTypes.KEYWORDS ? KEYWORDS_GAP_VALUE_KEY : VOLUME_GAP_VALUE_KEY;

        const getDomainData = (item) => ({
            domain: {
                name: item.name,
                color: item.borderColor || item.color || DEFAULT_COLOR,
            },
            value: item[valueKey] || 0,
        });

        const intersectionSets = point.sets;

        const tooltipData = !isIntersection
            ? [getDomainData(point)]
            : allData.filter((item) => !item.hideInLegend && intersectionSets.includes(item.id)).map(getDomainData);

        const columnDefinition = [
            {
                key: "domain",
                title: translate("Domain"),
                align: "left" as const,
                selector: "domain" as keyof ITooltipRowData,
                size: "130px",
                render: ({ domain }: { domain: { name: string; color: string } }) => {
                    return (
                        <ChartTooltip.LabelCell
                            label={domain.name}
                            backgroundColor={domain.color}
                        />
                    );
                },
            },
            {
                key: "value",
                title:
                    selectedMetricIndex === MetricTypes.KEYWORDS
                        ? translate("common.keywords")
                        : translate("common.volume"),
                align: "right" as const,
                selector: "value" as keyof ITooltipRowData,
                size: "80px",
                render: ({ value }: { value: number }) => {
                    const formattedValue = getFormattedValue(value, valueKey);
                    return <ChartTooltip.ValueCell value={formattedValue} />;
                },
            },
        ];

        const tooltipContent = (
            <ChartTooltipWrapper isIntersection={isIntersection}>
                <ChartTooltip<ITooltipRowData>
                    width={230}
                    data={tooltipData}
                    columnDefinition={columnDefinition}
                    title={
                        isIntersection ? (
                            <CustomTitle
                                title={translate("wa.traffic.sources.search.seo.overview.keywords.gap.tooltip.title")}
                                value={getFormattedValue(Number(point[valueKey]), valueKey)}
                            />
                        ) : null
                    }
                />
            </ChartTooltipWrapper>
        );

        return renderToString(tooltipContent);
    };
};
